import type {
  ApprovalsScope,
  CollaborateListView,
  CommentListFilter,
  Thread,
  UserId,
} from "../types/collaborateThread";
import { canCurrentUserApproveThread, isSameUserId, threadNeedsSignoff } from "./signoffAccess";

function bodyMentionsUser(body: string, userId: UserId): boolean {
  if (!userId || !body) {
    return false;
  }
  const escaped = userId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`<mention\\s+id=["']${escaped}["']`, "i").test(body);
}

/** True when the current user is @mentioned on the root comment or a reply. */
export function threadMentionsUser(thread: Thread, currentUserId: UserId): boolean {
  if (thread.mentions?.some((id) => isSameUserId(id, currentUserId))) {
    return true;
  }
  if (bodyMentionsUser(thread.body, currentUserId)) {
    return true;
  }
  return thread.replies.some(
    (r) =>
      r.mentions?.some((id) => isSameUserId(id, currentUserId)) ||
      bodyMentionsUser(r.body, currentUserId),
  );
}

export function filterMentionThreads(threads: Thread[], currentUserId: UserId): Thread[] {
  return threads.filter((t) => threadMentionsUser(t, currentUserId));
}

const MENTION_TAG_RE = /<mention[\s>]/i;

/** True when the thread (root or reply) includes any @mention — of you or someone else. */
export function threadHasAnyMention(thread: Thread): boolean {
  if (thread.mentions && thread.mentions.length > 0) {
    return true;
  }
  if (MENTION_TAG_RE.test(thread.body)) {
    return true;
  }
  return thread.replies.some(
    (r) => (r.mentions && r.mentions.length > 0) || MENTION_TAG_RE.test(r.body),
  );
}

/**
 * Unread sort rank (lower = higher in list):
 * 0 sign-offs assigned to you
 * 1 mentions of you
 * 2 other people’s sign-offs
 * 3 mentions of others
 * 4 everything else
 */
export function unreadSortRank(thread: Thread, currentUserId: UserId): number {
  const isSignoff = threadNeedsSignoff(thread) || /<signoff[\s>]/i.test(thread.body);
  if (isSignoff && canCurrentUserApproveThread(thread, currentUserId)) {
    return 0;
  }
  if (threadMentionsUser(thread, currentUserId)) {
    return 1;
  }
  if (isSignoff) {
    return 2;
  }
  if (threadHasAnyMention(thread)) {
    return 3;
  }
  return 4;
}

/** Stable unread ordering for the Unread tab. */
export function sortUnreadThreadsByPriority(threads: Thread[], currentUserId: UserId): Thread[] {
  return threads
    .map((thread, index) => ({ thread, index, rank: unreadSortRank(thread, currentUserId) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ thread }) => thread);
}

export function filterCommentThreads(
  threads: Thread[],
  filter: CommentListFilter,
  currentUserId: UserId,
): Thread[] {
  if (filter === "open") {
    return threads.filter((t) => t.status === "pending");
  }
  if (filter === "mine") {
    return threads.filter(
      (t) =>
        isSameUserId(t.createdBy.id, currentUserId) ||
        isSameUserId(t.assignee?.id, currentUserId),
    );
  }
  return threads;
}

export function filterApprovalThreads(
  threads: Thread[],
  scope: ApprovalsScope,
  currentUserId: UserId,
): Thread[] {
  const approvalOnly = threads.filter(threadNeedsSignoff);
  if (scope === "mine") {
    return approvalOnly.filter(
      (t) => t.status === "pending" && isSameUserId(t.assignee?.id, currentUserId),
    );
  }
  if (scope === "requested") {
    return approvalOnly.filter(
      (t) => isSameUserId(t.createdBy.id, currentUserId) && t.status === "pending",
    );
  }
  return approvalOnly;
}

export function countCommentsTab(threads: Thread[]): number {
  return threads.length;
}

export function countApprovalsTab(threads: Thread[]): number {
  return threads.filter(threadNeedsSignoff).length;
}

export function filterThreadsByListView(
  threads: Thread[],
  view: CollaborateListView,
  currentUserId: UserId,
): Thread[] {
  if (view === "comments") {
    return threads;
  }
  if (view === "unread") {
    return threads.filter((t) => t.unread === true);
  }
  if (view === "mentions") {
    return filterMentionThreads(threads, currentUserId);
  }
  if (view === "pending-mine") {
    return filterApprovalThreads(threads, "mine", currentUserId);
  }
  if (view === "requested-by-me") {
    return filterApprovalThreads(threads, "requested", currentUserId);
  }
  return filterApprovalThreads(threads, "all", currentUserId);
}

export function countThreadsForListView(
  threads: Thread[],
  view: CollaborateListView,
  currentUserId: UserId,
): number {
  return filterThreadsByListView(threads, view, currentUserId).length;
}

export function filterThreadsByVersion(
  threads: Thread[],
  versionFilter: number | null,
): Thread[] {
  if (versionFilter === null) {
    return threads;
  }
  return threads.filter((t) => t.versionNumber === versionFilter);
}

export function getThreadVersionNumbers(threads: Thread[]): number[] {
  const versions = new Set<number>();
  for (const thread of threads) {
    if (Number.isFinite(thread.versionNumber)) {
      versions.add(thread.versionNumber);
    }
  }
  return Array.from(versions).sort((a, b) => a - b);
}

export function applyCollaborateThreadFilters(
  threads: Thread[],
  view: CollaborateListView,
  versionFilter: number | null,
  currentUserId: UserId,
): Thread[] {
  const filtered = filterThreadsByVersion(
    filterThreadsByListView(threads, view, currentUserId),
    versionFilter,
  );
  if (view === "unread") {
    return sortUnreadThreadsByPriority(filtered, currentUserId);
  }
  return filtered;
}

export function countThreadsForVersionFilter(
  threads: Thread[],
  view: CollaborateListView,
  versionFilter: number | null,
  currentUserId: UserId,
): number {
  return applyCollaborateThreadFilters(threads, view, versionFilter, currentUserId).length;
}

export function countReviewAssignedToMe(threads: Thread[], currentUserId: UserId): number {
  return filterOnYouThreads(threads, currentUserId).length;
}

/** Pending sign-offs assigned to the current user. */
export function filterOnYouThreads(threads: Thread[], currentUserId: UserId): Thread[] {
  return threads.filter(
    (t) =>
      threadNeedsSignoff(t) &&
      t.status === "pending" &&
      isSameUserId(t.assignee?.id, currentUserId),
  );
}

const ON_YOU_CHIP_MAX = 26;

/** Short label for On You summary chips (section ref or truncated quote). */
export function formatOnYouChipLabel(thread: Thread, maxLen = ON_YOU_CHIP_MAX): string {
  const plainBody = thread.body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const source = `${thread.quote} ${plainBody}`.trim();
  const sectionMatch = source.match(/§\s*[\w.]+(?:\s+[\w][\w\s-]*)?/);
  if (sectionMatch) {
    const label = sectionMatch[0].trim();
    return label.length > maxLen ? `${label.slice(0, maxLen)}…` : label;
  }
  const label = thread.quote.trim() || plainBody;
  if (!label) {
    return "Sign-off";
  }
  return label.length > maxLen ? `${label.slice(0, maxLen)}…` : label;
}
